import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import dayjs from "dayjs"
import {
  Alert, Avatar, Button, Chip, Divider, IconButton, ImageList, ImageListItem, ImageListItemBar,
  List, ListItem, ListItemText, Paper, TextField, Typography
} from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { alertError } from "components/Alert"
import { CameraAltRounded, CloseRounded, EditRounded } from "resource/muiImport"
import { resizeImage } from "./resizeImage"

// job_order_file_type ids; 14 (เอกสาร) is the EIR slip Gemini reads, the rest are extra photos
const PHOTO_TYPES = [
  { id: 14, label: "เอกสาร EIR", required: true, hint: "ใบรับ-ส่งตู้จากท่า/ลาน" },
  { id: 1, label: "หมายเลขตู้" },
  { id: 2, label: "หมายเลขซีล" },
  { id: 3, label: "สภาพตู้" },
  { id: 4, label: "อื่นๆ" },
]
const MAX_FILES = 20

const emptyPhotos = () => Object.fromEntries(PHOTO_TYPES.map(t => [t.id, []]))

/**
 * /liff/upload — the driver photographs the EIR slip (plus optional container photos),
 * the server reads Bkg/ContNo/SealNo with Gemini and attaches the photos to the job order
 * if the rule in module/line-sub matches; otherwise it shows why and an operator finishes
 * it on page 1.4.1.
 */
const LiffUpload = ({ liff }) => {
  const { ax, isLoading } = useContext(GlobalStateContext)
  const history = useHistory()
  const { profile, idToken } = liff
  const [photos, setPhotos] = useState(emptyPhotos)
  const [rmk, setRmk] = useState("")
  const [result, setResult] = useState(null)
  const [history_, setHistory_] = useState([])
  const [busy, setBusy] = useState(false)

  const previews = useMemo(() => {
    const map = {}
    for (const typ of PHOTO_TYPES) {
      map[typ.id] = photos[typ.id].map(f => URL.createObjectURL(f))
    }
    return map
  }, [photos])
  useEffect(() => () => { Object.values(previews).flat().forEach(URL.revokeObjectURL) }, [previews])

  const loadHistory = useCallback(() => {
    ax.post("/no-session/line-sub/getLineEirHistory", { IdToken: idToken }, false).then(res => {
      if (res.data) setHistory_(res.data)
    })
  }, [ax, idToken])
  useEffect(() => { loadHistory() }, [loadHistory])

  const numFiles = useMemo(() => Object.values(photos).reduce((s, arr) => s + arr.length, 0), [photos])

  const handlePick = useCallback(async (typId, fileList) => {
    const picked = Array.from(fileList || [])
    if (picked.length === 0) return
    if (numFiles + picked.length > MAX_FILES) {
      alertError(`อัปโหลดได้ครั้งละไม่เกิน ${MAX_FILES} รูป`)
      return
    }
    setBusy(true)
    try {
      const resized = await Promise.all(picked.map(f => resizeImage(f)))
      setPhotos(o => ({ ...o, [typId]: [...o[typId], ...resized] }))
    } catch (e) {
      console.error(e)
      alertError("ย่อรูปไม่สำเร็จ กรุณาลองใหม่")
    } finally {
      setBusy(false)
    }
  }, [numFiles])

  const handleRemove = useCallback((typId, idx) => {
    setPhotos(o => ({ ...o, [typId]: o[typId].filter((_, i) => i !== idx) }))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (photos[14].length === 0) {
      alertError("กรุณาถ่ายรูปเอกสาร EIR อย่างน้อย 1 รูป")
      return
    }
    const formData = new FormData()
    const fileTyps = []
    const labels = []
    for (const typ of PHOTO_TYPES) {
      for (const f of photos[typ.id]) {
        formData.append("files", f)
        fileTyps.push(typ.id)
        labels.push(typ.label)
      }
    }
    formData.append("FileTyps", JSON.stringify(fileTyps))
    formData.append("Labels", JSON.stringify(labels))
    formData.append("Rmk", rmk)
    formData.append("IdToken", idToken)
    const res = await ax.post("/no-session/line-sub/uploadLineEir", formData)
    if (res.error?.code === -2) {
      alertError(res.error.message)
      history.replace("/liff/register")
      return
    }
    if (!res.data) return
    setResult(res.data)
    setPhotos(emptyPhotos())
    setRmk("")
    loadHistory()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [ax, photos, rmk, idToken, history, loadHistory])

  return (
    <BoxFC p={2} gap={2} maxWidth={560} mx="auto">
      <BoxFR gap={1.5} alignItems="center">
        <Avatar src={profile?.PicUrl || undefined} sx={{ width: 44, height: 44 }} />
        <BoxFC gap={0} flex={1}>
          <Typography fontWeight="bold">{profile.FName} {profile.LName}</Typography>
          <Typography variant="body2" color="text.secondary">{profile.ScNm} · {profile.SiteSNm} · {profile.LicNm}</Typography>
        </BoxFC>
        <IconButton size="small" onClick={() => history.push("/liff/register")}><EditRounded fontSize="small" /></IconButton>
      </BoxFR>

      {result && (
        <Alert severity={result.IsMch ? "success" : "warning"} onClose={() => setResult(null)}>
          <Typography fontWeight="bold">
            {result.IsMch ? `บันทึกเข้าใบงาน ${result.JobOrdId}${result.JobNo ? ` (${result.JobNo})` : ""} แล้ว` : "รับรูปแล้ว รอเจ้าหน้าที่จับคู่ใบงาน"}
          </Typography>
          <Typography variant="body2">
            Booking: {result.Bkg || "-"} · ตู้: {result.ContNo || "-"} · ซีล: {result.SealNo || "-"} · {result.NumFile} รูป
          </Typography>
          {!result.IsMch && result.MchRmk && <Typography variant="body2">สาเหตุ: {result.MchRmk}</Typography>}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <BoxFC gap={2}>
          <Typography variant="h6">อัปโหลด EIR</Typography>
          {PHOTO_TYPES.map(typ => (
            <BoxFC key={typ.id} gap={1}>
              <BoxFR alignItems="center" gap={1}>
                <Typography fontWeight={typ.required ? "bold" : "normal"}>
                  {typ.label}{typ.required && <Typography component="span" color="error"> *</Typography>}
                </Typography>
                {typ.hint && <Typography variant="caption" color="text.secondary">{typ.hint}</Typography>}
                <BoxFC flex={1} />
                <Button component="label" size="small" variant="outlined" startIcon={<CameraAltRounded />} disabled={busy}>
                  ถ่าย/เลือกรูป
                  <input hidden type="file" accept="image/*" multiple
                    onChange={e => { handlePick(typ.id, e.target.files); e.target.value = "" }} />
                </Button>
              </BoxFR>
              {photos[typ.id].length > 0 && (
                <ImageList cols={3} gap={6} sx={{ m: 0 }}>
                  {photos[typ.id].map((f, i) => (
                    <ImageListItem key={i} sx={{ borderRadius: 1, overflow: "hidden" }}>
                      <img src={previews[typ.id][i]} alt={f.name} loading="lazy" style={{ aspectRatio: "1", objectFit: "cover" }} />
                      <ImageListItemBar position="top" sx={{ background: "transparent" }}
                        actionIcon={
                          <IconButton size="small" onClick={() => handleRemove(typ.id, i)}
                            sx={{ color: "white", bgcolor: "rgba(0,0,0,0.45)", m: 0.5 }}>
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        } />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </BoxFC>
          ))}
          <TextField label="หมายเหตุ" size="small" fullWidth multiline minRows={2}
            value={rmk} onChange={e => setRmk(e.target.value)} />
          <Button variant="contained" size="large" onClick={handleSubmit} disabled={busy || isLoading || numFiles === 0}>
            ส่งรูป ({numFiles})
          </Button>
        </BoxFC>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">รายการล่าสุด</Typography>
        <Divider sx={{ my: 1 }} />
        {history_.length === 0 && <Typography variant="body2" color="text.secondary">ยังไม่มีรายการ</Typography>}
        <List dense disablePadding>
          {history_.map(h => (
            <ListItem key={h.ScDrvEirId} disableGutters
              secondaryAction={
                h.Sts === 1
                  ? <Chip size="small" color="success" label={`ใบงาน ${h.JobOrdId}`} />
                  : <Chip size="small" color="warning" label="รอจับคู่" />
              }>
              <ListItemText
                primary={`${h.ContNo || "-"} · ${h.Bkg || "-"}`}
                secondary={`${dayjs(h.UpldTm).format("DD/MM HH:mm")} · ${h.NumFile} รูป${h.Sts === 1 && h.JobNo ? ` · ${h.JobNo}` : ""}${h.Sts !== 1 && h.MchRmk ? ` · ${h.MchRmk}` : ""}`}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </BoxFC>
  )
}

export default LiffUpload
